import {
  CalendarDays,
  Clock3,
  CreditCard,
  FileJson,
  Package,
  ReceiptText,
  Truck,
} from 'lucide-react';
import FormModal from '../../../components/Modals/FormModal';
import { RestaurantPurchaseOrderDTO } from '../../../types/restaurant/RestaurantPurchaseOrderTypes';
import { formatMoney } from '../../../utils/decimalValue';
import { tipoDteLabel } from './dteImport/parseDtePurchase';

interface Props {
  purchase: RestaurantPurchaseOrderDTO | null;
  isOpen: boolean;
  onClose: () => void;
}

const statusLabel = {
  DRAFT: 'Borrador',
  CONFIRMED: 'Confirmada',
  CANCELLED: 'Cancelada',
} as const;

const formatDate = (value: string | null | undefined) =>
  value
    ? new Date(`${value}T00:00:00`).toLocaleDateString('es-SV')
    : '—';

const paymentTerm = (
  purchase: RestaurantPurchaseOrderDTO
) => {
  const condition = purchase.paymentCondition;
  if (!condition || condition.code !== 'CREDITO') return 'No aplica';

  const amount =
    condition.termCode === '01'
      ? condition.creditDays
      : condition.termCode === '02'
      ? condition.creditMonths
      : condition.creditYears;

  return amount && condition.termName
    ? `${amount} ${condition.termName.toLowerCase()}`
    : '—';
};

const FiscalSummary = ({
  purchase,
}: {
  purchase: RestaurantPurchaseOrderDTO;
}) => {
  const rows: Array<[string, number]> = [
    ['Gravado', purchase.dteTaxedTotal],
    ['Exento', purchase.dteExemptTotal],
    ['No sujeto', purchase.dteNonSubjectTotal],
    ['Descuento', purchase.dteDiscountTotal],
    ['IVA 13%', purchase.dteIvaTotal],
    ['IVA percibido', purchase.dteIvaPerception],
    ['IVA retenido', purchase.dteIvaRetention],
    ['Retención renta', purchase.dteIncomeRetention],
    ['Monto de la operación', purchase.dteOperationTotal],
  ].filter(([, value]) => Number(value) !== 0) as Array<[string, number]>;

  return (
    <div className="rounded-xl border border-stroke dark:border-strokedark">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stroke px-4 py-3 dark:border-strokedark">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileJson className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-black dark:text-white">
              Documento fiscal · {tipoDteLabel(purchase.dteType || '')}
            </p>
            <p className="font-mono text-[11px] text-bodydark2">
              {purchase.dteControlNumber}
              {purchase.dteGenerationCode
                ? ` · ${purchase.dteGenerationCode}`
                : ''}
            </p>
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 px-4 py-3 text-sm sm:grid-cols-3">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2">
            <dt className="text-bodydark2">{label}</dt>
            <dd className="font-medium text-black dark:text-white">
              {formatMoney(value)}
            </dd>
          </div>
        ))}
        <div className="col-span-2 mt-1 flex justify-between gap-2 border-t border-stroke pt-1.5 sm:col-span-3 dark:border-strokedark">
          <dt className="font-semibold text-black dark:text-white">
            Total a pagar
          </dt>
          <dd className="font-bold text-black dark:text-white">
            {formatMoney(purchase.dtePayableTotal)}
          </dd>
        </div>
      </dl>
    </div>
  );
};

const RestaurantPurchaseOrderDetailModal = ({
  purchase,
  isOpen,
  onClose,
}: Props) => (
  <FormModal
    isOpen={isOpen}
    onClose={onClose}
    title="Detalle de compra"
    maxWidth="max-w-5xl"
  >
    {purchase && (
      <div className="space-y-5">
        <div className="flex flex-col gap-4 rounded-xl border border-stroke bg-gray-2/40 p-5 dark:border-strokedark dark:bg-meta-4/20 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">
              {purchase.purchaseNumber}
            </p>
            <h3 className="mt-1 text-xl font-semibold text-black dark:text-white">
              {purchase.supplierName}
            </h3>
            <p className="mt-2 flex items-center gap-2 text-sm text-bodydark2">
              <CalendarDays className="h-4 w-4" />
              {new Date(`${purchase.purchaseDate}T00:00:00`).toLocaleDateString(
                'es-SV'
              )}
            </p>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-sm font-semibold ${
              purchase.status === 'CONFIRMED'
                ? 'bg-success/10 text-success'
                : purchase.status === 'CANCELLED'
                ? 'bg-danger/10 text-danger'
                : 'bg-warning/10 text-warning'
            }`}
          >
            {statusLabel[purchase.status]}
          </span>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <Truck className="mb-2 h-5 w-5 text-primary" />
            <p className="text-xs text-bodydark2">Sucursal</p>
            <p className="font-semibold text-black dark:text-white">
              {purchase.branchName}
            </p>
          </div>
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <Package className="mb-2 h-5 w-5 text-primary" />
            <p className="text-xs text-bodydark2">Detalles</p>
            <p className="font-semibold text-black dark:text-white">
              {purchase.detailCount}
            </p>
          </div>
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <ReceiptText className="mb-2 h-5 w-5 text-primary" />
            <p className="text-xs text-bodydark2">Total</p>
            <p className="text-lg font-bold text-black dark:text-white">
              {formatMoney(purchase.total)}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <CreditCard className="mb-2 h-5 w-5 text-primary" />
            <p className="text-xs text-bodydark2">Condición de pago</p>
            <p className="font-semibold text-black dark:text-white">
              {purchase.paymentCondition?.name || '—'}
            </p>
          </div>
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <CalendarDays className="mb-2 h-5 w-5 text-primary" />
            <p className="text-xs text-bodydark2">Fecha de vencimiento</p>
            <p className="font-semibold text-black dark:text-white">
              {formatDate(purchase.paymentCondition?.creditDueDate)}
            </p>
          </div>
          <div className="rounded-lg border border-stroke p-4 dark:border-strokedark">
            <Clock3 className="mb-2 h-5 w-5 text-primary" />
            <p className="text-xs text-bodydark2">Plazo</p>
            <p className="font-semibold text-black dark:text-white">
              {paymentTerm(purchase)}
            </p>
          </div>
        </div>
        {purchase.dteControlNumber && <FiscalSummary purchase={purchase} />}
        <div className="overflow-x-auto rounded-xl border border-stroke dark:border-strokedark">
          <table
            className={`w-full text-left text-sm ${
              purchase.dteControlNumber ? 'min-w-[1150px]' : 'min-w-[900px]'
            }`}
          >
            <thead className="bg-gray-2 text-xs uppercase text-bodydark2 dark:bg-meta-4">
              <tr>
                <th className="p-3">Materia prima</th>
                <th className="p-3">Cantidad</th>
                <th className="p-3">Costo</th>
                <th className="p-3">Descuento</th>
                <th className="p-3">Impuesto</th>
                {purchase.dteControlNumber && (
                  <>
                    <th className="p-3">Gravado DTE</th>
                    <th className="p-3">Exento DTE</th>
                    <th className="p-3">No sujeto DTE</th>
                  </>
                )}
                <th className="p-3">Lote / vencimiento</th>
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stroke dark:divide-strokedark">
              {(purchase.details ?? []).map((item) => (
                <tr key={item.id}>
                  <td className="p-3 font-medium text-black dark:text-white">
                    {item.rawMaterialCode} · {item.rawMaterialName}
                  </td>
                  <td className="p-3">
                    {Number(item.quantity).toLocaleString('es-SV')}{' '}
                    {item.unitCode}
                  </td>
                  <td className="p-3">{formatMoney(item.unitCost)}</td>
                  <td className="p-3">
                    {formatMoney(
                      purchase.dteControlNumber &&
                        Number(item.discountAmount) === 0
                        ? item.dteDiscountAmount
                        : item.discountAmount
                    )}
                  </td>
                  <td className="p-3">{formatMoney(item.taxAmount)}</td>
                  {purchase.dteControlNumber && (
                    <>
                      <td className="p-3">{formatMoney(item.dteTaxedSale)}</td>
                      <td className="p-3">{formatMoney(item.dteExemptSale)}</td>
                      <td className="p-3">
                        {formatMoney(item.dteNonSubjectSale)}
                      </td>
                    </>
                  )}
                  <td className="p-3">
                    {item.lotNumber || '—'}
                    {item.expirationDate && (
                      <span className="block text-xs text-bodydark2">
                        Vence:{' '}
                        {new Date(
                          `${item.expirationDate}T00:00:00`
                        ).toLocaleDateString('es-SV')}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right font-semibold">
                    {formatMoney(item.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ml-auto grid max-w-sm grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <span>Subtotal</span>
          <strong className="text-right">
            {formatMoney(purchase.subtotal)}
          </strong>
          <span>Descuentos</span>
          <strong className="text-right text-danger">
            − {formatMoney(purchase.discountTotal)}
          </strong>
          <span>Impuestos</span>
          <strong className="text-right">
            {formatMoney(purchase.taxTotal)}
          </strong>
          <span className="border-t border-stroke pt-2 text-base dark:border-strokedark">
            Total
          </span>
          <strong className="border-t border-stroke pt-2 text-right text-base text-primary dark:border-strokedark">
            {formatMoney(purchase.total)}
          </strong>
        </div>
        {purchase.notes && (
          <div className="rounded-lg border border-stroke p-4 text-sm dark:border-strokedark">
            <p className="mb-1 font-semibold text-black dark:text-white">
              Notas
            </p>
            <p className="whitespace-pre-wrap">{purchase.notes}</p>
          </div>
        )}
      </div>
    )}
  </FormModal>
);

export default RestaurantPurchaseOrderDetailModal;
